import React from "react";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { PageHeader, SectionLabel, SectionTitle } from "../components/Section";
import { approach } from "../data/catalog";
import { CONTACT } from "../data/contact";

const About = () => {
  return (
    <div data-testid="about-page">
      <PageHeader
        testId="about-header"
        eyebrow="About / SPINLAB"
        title={<>Shop-quality detailing,<br />brought to your driveway.</>}
        description="Professional mobile detailing that saves you time without compromising the quality of the work."
      />

      {/* SERGEI NOTE */}
      <section
        data-testid="about-who-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-4">
            <SectionLabel number="01" testId="about-who-label">A Note From Me</SectionLabel>
          </div>
          <div className="lg:col-span-8 space-y-7">
            <SectionTitle testId="about-who-title">
              Hi, I&apos;m Sergei —<br />the owner and detailer<br />behind SPINLAB.
            </SectionTitle>
            <p className="text-base sm:text-lg font-light leading-relaxed text-gray-400 max-w-2xl">
              SPINLAB is a one-person, owner-operated mobile detailing business
              based in {CONTACT.city}. I personally handle every vehicle from
              start to finish, bringing a fully equipped detailing setup
              directly to your driveway.
            </p>
            <p className="text-base sm:text-lg font-light leading-relaxed text-gray-400 max-w-2xl">
              I bring my own water, power and professional equipment, so you
              can have your vehicle properly detailed without driving anywhere,
              waiting at a shop or rearranging your day.
            </p>
            <p className="text-base sm:text-lg font-light leading-relaxed text-gray-400 max-w-2xl">
              I focus on careful, thorough work and give every vehicle the time
              and attention it actually needs.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="about-cta-quote"
                className="inline-flex justify-center px-10 py-4 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all"
              >
                Request a Quote
              </Link>
              <a
                href={CONTACT.smsHref}
                data-testid="about-cta-call"
                className="inline-flex justify-center items-center gap-2 px-10 py-4 border border-gray-800 text-[11px] font-display font-light uppercase tracking-[0.3em] text-gray-300 hover:text-white hover:border-gray-500 transition-all"
              >
                <MessageSquare strokeWidth={1} size={14} /> Text {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOW I WORK */}
      <section
        data-testid="about-approach-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900"
      >
        <SectionLabel number="02" testId="about-approach-label">How I work</SectionLabel>
        <SectionTitle testId="about-approach-title" className="max-w-3xl">
          Every car is different.<br />Every quote is honest.
        </SectionTitle>
        <p className="mt-8 text-sm sm:text-base font-light leading-relaxed text-gray-400 max-w-2xl">
          I&apos;ll provide a clear quote before the appointment, take the
          time the job actually deserves, and give the vehicle the kind of
          attention to detail that&apos;s missing from most car-wash-style
          operations.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-900">
          {approach.map((item, i) => (
            <div
              key={i}
              data-testid={`approach-item-${i}`}
              className="bg-black p-8 md:p-12 flex items-center gap-6"
            >
              <span className="text-[10px] font-display font-light tracking-[0.4em] text-gray-600 w-8 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm md:text-base font-display font-light uppercase tracking-[0.25em] text-white">
                {item}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 text-center">
        <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.2]">
          A proper detail.<br />Delivered to your driveway.
        </h2>
        <Link
          to="/contact"
          data-testid="about-cta-bottom"
          className="mt-10 inline-flex px-12 py-5 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all"
        >
          Request a Quote
        </Link>
      </section>
    </div>
  );
};

export default About;
